/**
 * `/login/link-invalid` — where `GET /api/auth/redeem` sends a browser when the peeked one-time
 * link is expired, already used or unknown (PRD §8, PLAN.md D-15). POST `/api/auth/redeem`
 * failures land here via the confirm page. The reason arrives as a query parameter; the copy
 * comes from the same error codes the API uses everywhere else.
 */

import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { GrantonomyLogo } from "@/components/g-logo";
import { PublicLayout } from "@/components/public-layout";

const REASONS = ["expired", "used", "invalid"] as const;
type Reason = (typeof REASONS)[number];

function reasonFrom(value: string | null): Reason {
  return REASONS.includes(value as Reason) ? (value as Reason) : "invalid";
}

export function LinkInvalidPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const reason = reasonFrom(params.get("reason"));

  return (
    <PublicLayout>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <GrantonomyLogo />
          </div>
          <h1 className="font-display text-4xl">{t("linkInvalid.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t(`linkInvalid.${reason}`)}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/login/forgot">{t("linkInvalid.requestNew")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/login">{t("common.signIn")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/employee-login">{t("linkInvalid.employeeLogin")}</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
